/**
 * Companies List — "متابعة ملف الشركات" (Feb 2026 / iter-177).
 * GET /api/agent/companies → cards showing name + license number + expiry.
 * Click a card → detail page with residencies.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Plus, Building2, Search, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { AgentPageShell, ExpiryBadge } from './agent/AgentPageShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentCompaniesListPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'متابعة ملف الشركات', label_en: 'Track Company Files', path: null },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/agent/companies`, {
        headers: getAuthHeaders(),
      });
      setRows(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // iter-196 — handleDelete removed from the card. The company detail page
  // still owns the destructive action (Trash button inside the file view).

  const filtered = rows.filter((c) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      (c.company_name_ar || '').toLowerCase().includes(needle) ||
      (c.company_name_en || '').toLowerCase().includes(needle) ||
      (c.license_number || '').toLowerCase().includes(needle) ||
      (c.unified_license_number || '').toLowerCase().includes(needle)
    );
  });

  return (
    <AgentPageShell
      breadcrumb={breadcrumb}
      onBack={() => navigate('/manage/cloudgm-agent')}
    >
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-white">
          {language === 'ar' ? 'متابعة ملف الشركات' : 'Track Company Files'}
        </h1>
        <div className="flex gap-3 flex-wrap">
          <Button
            onClick={() => navigate('/manage/cloudgm-agent/company/new')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            data-testid="agent-new-company-btn"
          >
            <Plus className="h-4 w-4 me-2" />
            {language === 'ar' ? 'إنشاء ملف شركة' : 'Create Company File'}
          </Button>
        </div>
      </div>

      {/* search */}
      <div className="relative mb-6 max-w-md">
        <Search className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#606060]" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={language === 'ar' ? 'ابحث بالاسم أو رقم الرخصة…' : 'Search by name or license number…'}
          className="pe-9"
          data-testid="agent-company-search"
        />
      </div>

      {loading ? (
        <div className="text-center text-[#808080] py-16">{language === 'ar' ? 'جاري التحميل…' : 'Loading…'}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl">
          <Building2 className="h-10 w-10 text-[#606060] mx-auto mb-3" />
          <p className="text-[#808080] mb-4">
            {language === 'ar' ? 'لا توجد شركات مسجّلة بعد' : 'No companies yet'}
          </p>
          <Button
            onClick={() => navigate('/manage/cloudgm-agent/company/new')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
          >
            <Plus className="h-4 w-4 me-2" />
            {language === 'ar' ? 'أنشئ أول ملف شركة' : 'Create the first company file'}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c, i) => (
            <motion.button
              key={c.id}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              onClick={() => navigate(`/manage/cloudgm-agent/companies/${c.id}`)}
              className="group text-start bg-white/[0.02] hover:bg-white/[0.04] border border-white/10 hover:border-emerald-500/40 rounded-2xl p-5 transition-all"
              data-testid={`agent-company-card-${c.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="w-11 h-11 mb-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-emerald-400" />
                  </div>
                  <h3 className="font-bold truncate text-white">
                    {language === 'ar' ? (c.company_name_ar || c.company_name_en) : (c.company_name_en || c.company_name_ar)}
                  </h3>
                  {(language === 'ar' ? c.company_name_en : c.company_name_ar) && (
                    <p className="text-xs text-[#808080] truncate mt-0.5">
                      {language === 'ar' ? c.company_name_en : c.company_name_ar}
                    </p>
                  )}
                </div>
                {/* iter-196 — Delete button moved INSIDE the file detail page.
                    Card no longer exposes a destructive action to avoid
                    accidental clicks. */}
              </div>
              <div className="mt-4 space-y-1 text-xs text-[#808080]">
                {c.license_number && (
                  <div><span className="text-[#606060]">{language === 'ar' ? 'رقم الرخصة:' : 'License #:'}</span> {c.license_number}</div>
                )}
                {c.issuing_authority && (
                  <div><span className="text-[#606060]">{language === 'ar' ? 'الجهة:' : 'Authority:'}</span> {c.issuing_authority}</div>
                )}
                {c.expiry_date && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Calendar className="h-3 w-3" />
                    <span>{language === 'ar' ? 'ينتهي:' : 'Expiry:'} {c.expiry_date}</span>
                    <ExpiryBadge dateStr={c.expiry_date} language={language} />
                  </div>
                )}
              </div>
            </motion.button>
          ))}
        </div>
      )}
    </AgentPageShell>
  );
};

export default AgentCompaniesListPage;
